import React, { useState, useEffect, useRef, useCallback, FormEvent, useEffect as useReactEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchConversations, fetchMessages, sendMessage, toggleBotForConversation, updateClientNickname, markAsSeen, blockContactByPhone, unblockContactByPhone } from '../services/api.ts';
import { Conversation } from '../types';
import ConversationsListPanel from '../components/conversations/ConversationsListPanel.tsx';
import ConversationsChatHeader from '../components/conversations/ConversationsChatHeader.tsx';
import ConversationsMessageThread from '../components/conversations/ConversationsMessageThread.tsx';
import ConversationsComposer from '../components/conversations/ConversationsComposer.tsx';
import ConversationsEmptyState from '../components/conversations/ConversationsEmptyState.tsx';
import ConversationsMobileToggle from '../components/conversations/ConversationsMobileToggle.tsx';
import ConversationsImprovementModal from '../components/conversations/ConversationsImprovementModal.tsx';
import ConversationsPhotoPreviewModal from '../components/conversations/ConversationsPhotoPreviewModal.tsx';
import Layout from '../components/layout/Layout.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { fetchShortcuts } from '../services/api.ts';
import {
  CONVERSATION_MESSAGES_POLL_MS,
  CONVERSATIONS_LIST_POLL_MS,
} from '../constants/timing.js';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { useChatMedia } from '../hooks/useChatMedia.ts';
import { useMessageImprovements } from '../hooks/useMessageImprovements.ts';
import { useOptimisticMessages } from '../hooks/useOptimisticMessages.ts';
import {
  sanitizeManualPhone,
  getDisplayName,
  filterShortcutsByPrefix,
} from './conversations.utils.ts';

interface Shortcut {
  id: number;
  atalho: string;
  mensagem: string;
  criado_em: string;
}

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);

    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
};

const Conversations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const forceAutoScrollRef = useRef(false);
  const messagesRequestRef = useRef(0);
  const latestMessagesResponseRef = useRef(0);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const { messages, resetMessages, addPendingMessage, confirmMessage, removePendingMessage, receiveMessages } = useOptimisticMessages();
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isReactivatingAll, setIsReactivatingAll] = useState(false);
  const [reactivateAllOpen, setReactivateAllOpen] = useState(false);
  const [newMessageModalOpen, setNewMessageModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const editContainerRef = useRef<HTMLDivElement>(null);
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockedOverride, setBlockedOverride] = useState<boolean | null>(null);
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isPhotoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [previewPhotoSrc, setPreviewPhotoSrc] = useState<string | null>(null);
  const [previewPhotoAlt, setPreviewPhotoAlt] = useState('Foto do cliente');
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');

  const loadMessages = useCallback(async (conversationId: string, options?: { scrollAfterLoad?: boolean }) => {
    const requestId = ++messagesRequestRef.current;
    try {
      const data = await fetchMessages(conversationId);
      if (currentConversationIdRef.current !== conversationId || requestId < latestMessagesResponseRef.current) return;
      latestMessagesResponseRef.current = requestId;
      receiveMessages(data);
      if (options?.scrollAfterLoad && typeof window !== 'undefined') {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        });
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      // Toast só na abertura da conversa — o poll não deve spammar o usuário.
      if (options?.scrollAfterLoad) {
        toast.error(getApiErrorMessage(error, 'Não foi possível carregar as mensagens.'));
      }
    }
  }, [receiveMessages]);

  const rawPhone = selectedConversation?.phoneNumber;
  const isBlocked = blockedOverride ?? !!(selectedConversation as any)?.blocked;

  const {
    isRecording,
    recordingMs,
    audioBlob,
    audioUrl,
    audioError,
    isAudioSending,
    showAudioPanel,
    handleStartRecording,
    handleStopRecording,
    handleCancelAudio,
    handleSendAudio,
    handleToggleAudioPanel,
    imageFile,
    imageUrl,
    imageError,
    isImageSending,
    showImagePanel,
    fileInputRef,
    handleImageSelected,
    handlePickPhoto,
    handleSendImage,
    handleCancelImage,
    handleToggleImagePanel,
  } = useChatMedia({
    phone: rawPhone,
    conversationId: selectedConversation?.id,
    resetKey: selectedConversation?.id,
    onMediaSent: () => selectedConversation?.id ? loadMessages(selectedConversation.id) : Promise.resolve(),
  });

  const {
    isImprovementOpen,
    improvementMessage,
    improvementText,
    setImprovementText,
    improvementError,
    improvementSuccess,
    isImprovementSending,
    improvementKind,
    handleOpenBotImprovement,
    handleOpenClientImprovement,
    handleCloseImprovement,
    handleSubmitImprovement,
  } = useMessageImprovements({ resetKey: selectedConversation?.id });

  const handleMobileToggle = (view: 'list' | 'chat') => {
    if (view === 'chat' && !selectedConversation) return;
    setMobileView(view);
  };

  useEffect(() => {
    resetMessages();
    latestMessagesResponseRef.current = ++messagesRequestRef.current;
    currentConversationIdRef.current = null;
    const fetchConversationsData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchConversations();
        setConversations(data);

        if (id) {
          const conversation = data.find(c => c.id === id);
          if (conversation) {
            setSelectedConversation(conversation);
            currentConversationIdRef.current = conversation.id;
            loadMessages(id, { scrollAfterLoad: true });
          } else {
            navigate('/atendabot/conversations');
          }
        } else {
          setSelectedConversation(null);
          resetMessages();
          currentConversationIdRef.current = null;
          lastMessageIdRef.current = null;
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversationsData();
  }, [id, navigate, loadMessages, resetMessages]);

  useEffect(() => {
    if (!isMobile) {
      setMobileView('list');
    }
  }, [isMobile]);

  useEffect(() => {
    if (mobileView === 'chat' && !selectedConversation) {
      setMobileView('list');
    }
  }, [mobileView, selectedConversation]);

  useEffect(() => {
    if (!messages.length) {
      lastMessageIdRef.current = null;
      return;
    }

    const latestMessageId = messages[messages.length - 1].id;
    const isNewMessage = latestMessageId !== lastMessageIdRef.current;

    if (isNewMessage) {
      const previousMessageId = lastMessageIdRef.current;
      const shouldForceImmediate = forceAutoScrollRef.current || previousMessageId === null;
      const scrollBehavior = shouldForceImmediate ? 'auto' : 'smooth';
      messagesEndRef.current?.scrollIntoView({ behavior: scrollBehavior });
      forceAutoScrollRef.current = false;
    }

    lastMessageIdRef.current = latestMessageId;
  }, [messages]);

  useEffect(() => {
    lastMessageIdRef.current = null;
    forceAutoScrollRef.current = true;
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (!selectedConversation?.id) return;
    markAsSeen(selectedConversation.id)
      .then(() => {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === selectedConversation.id ? { ...conv, unseen: false, unreadCount: 0 } : conv
          )
        );
        setSelectedConversation((prev) => (prev ? { ...prev, unseen: false, unreadCount: 0 } : prev));
      })
      .catch(() => {});
  }, [selectedConversation?.id]);


  useEffect(() => {
    if (!selectedConversation) return;
    currentConversationIdRef.current = selectedConversation.id;

    const interval = setInterval(() => {
      loadMessages(selectedConversation.id);
    }, CONVERSATION_MESSAGES_POLL_MS);

    return () => clearInterval(interval);
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const updated = await fetchConversations();
      setConversations(updated);

      if (selectedConversation) {
        const nova = updated.find(c => c.id === selectedConversation.id);
        if (nova) {
          setSelectedConversation({ ...selectedConversation, ...nova });
        }
      }
    }, CONVERSATIONS_LIST_POLL_MS);

    return () => clearInterval(interval);
  }, [selectedConversation]);
  
  useEffect(() => {
    const fetchShortcutsData = async () => {
      try {
        const data = await fetchShortcuts(0);
        setShortcuts(data);
      } catch (error) {
        console.error('Error fetching shortcuts:', error);
      }
    };
    fetchShortcutsData();
  }, []);

  useEffect(() => {
    if (!isPhotoPreviewOpen || typeof document === 'undefined') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPhotoPreviewOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPhotoPreviewOpen]);

  const handleReactivateAll = () => {
    setReactivateAllOpen(true);
  };

  const handleReactivateAllConfirm = async () => {
    setIsReactivatingAll(true);
    try {
      await api.post('/atendabot/conversas/reativar-todas', {});
      const data = await fetchConversations();
      setConversations(data);
      setReactivateAllOpen(false);
      toast.success('IA reativada em todas as conversas!');
    } catch (e) {
      toast.error('Erro ao reativar a IA em todas as conversas!');
    }
    setIsReactivatingAll(false);
  };

  const openPhotoPreview = (photo?: string | null, altText?: string) => {
    if (!photo) return;
    setPreviewPhotoSrc(photo);
    setPreviewPhotoAlt(altText?.trim() ? altText : 'Foto do cliente');
    setPhotoPreviewOpen(true);
  };

  const closePhotoPreview = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    setPhotoPreviewOpen(false);
    setPreviewPhotoSrc(null);
    setPreviewPhotoAlt('Foto do cliente');
  };

  const handlePhotoChange = useCallback((photo: string | null) => {
    setSelectedConversation((current) => current ? { ...current, photo: photo || undefined } : current);
    setConversations((current) => current.map((conversation) => (
      conversation.id === currentConversationIdRef.current
        ? { ...conversation, photo: photo || undefined }
        : conversation
    )));
  }, []);

  const handlePhotoClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (!selectedConversation?.photo) return;
    openPhotoPreview(selectedConversation.photo, getDisplayName(selectedConversation));
  };

  const handlePhotoKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!selectedConversation?.photo) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      openPhotoPreview(selectedConversation.photo, getDisplayName(selectedConversation));
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    resetMessages();
    latestMessagesResponseRef.current = ++messagesRequestRef.current;
    setSelectedConversation(conversation);
    currentConversationIdRef.current = conversation.id;
    lastMessageIdRef.current = null;
    forceAutoScrollRef.current = true;
    loadMessages(conversation.id, { scrollAfterLoad: true });
    window.history.replaceState(null, '', `/atendabot/conversations/${encodeURIComponent(conversation.id)}`);
    if (isMobile) {
      setMobileView('chat');
    }
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();

    if (!selectedConversation || !newMessage.trim()) return;

    const conversationId = selectedConversation.id;
    const content = newMessage;
    const pendingId = addPendingMessage(conversationId, content);
    setNewMessage('');

    try {
      const sentMessage = await sendMessage(conversationId, content);
      confirmMessage(pendingId, sentMessage);
    } catch (error) {
      removePendingMessage(pendingId);
      if (currentConversationIdRef.current === conversationId) {
        setNewMessage((current) => current || content);
      }
      console.error('Error sending message:', error);
      toast.error(getApiErrorMessage(error, 'Não foi possível enviar a mensagem.'));
    }
  };

  const handleToggleBot = async () => {
    if (!selectedConversation) return;

    try {
      const updated = await toggleBotForConversation(
        selectedConversation.id, 
        !selectedConversation.botActive
      );

      const updatedWithPreservedData: Conversation = {
        ...selectedConversation,
        botActive: updated.botActive
      };

      setSelectedConversation(updatedWithPreservedData);

      setConversations(
        conversations.map(conv =>
          conv.id === updated.id
            ? { ...conv, botActive: updated.botActive }
            : conv
        )
      );

    } catch (error) {
      console.error('Error toggling bot for conversation:', error);
    }
  };

  // NOVO: envio proativo
  const handleSendNewMessage = async ({ telefone, nome, mensagem }: { telefone: string; nome: string; mensagem: string }) => {
    try {
      const telefoneNormalizado = sanitizeManualPhone(telefone);
      await api.post('/atendabot/mensagens/manual', {
        telefone: telefoneNormalizado,
        nome,
        mensagem
      });
      setConversations(await fetchConversations());
      toast.success('Mensagem enviada!');
    } catch (e) {
      toast.error('Erro ao enviar mensagem: ' + (e instanceof Error ? e.message : 'Erro desconhecido'));
    }
  };

  const handleEditName = () => {
    if (selectedConversation) {
      setIsEditingName(true);
      setEditedName(getDisplayName(selectedConversation));
    }
  };

  const handleSaveName = async () => {
    if (!selectedConversation) return;

    try {
      // Se estiver vazio, envia null para usar o nome original
      const nicknameToSave = editedName.trim() === '' ? null : editedName.trim();
      await updateClientNickname(selectedConversation.id, nicknameToSave);

      // Update local state
      const updatedConversation = {
        ...selectedConversation,
        apelido: nicknameToSave
      };
      setSelectedConversation(updatedConversation);

      // Update conversations list
      setConversations(conversations.map(conv =>
        conv.id === selectedConversation.id
          ? { ...conv, apelido: nicknameToSave }
          : conv
      ));

      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating nickname:', error);
      toast.error('Erro ao atualizar o nome.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleToggleBlocked = async () => {
    if (!selectedConversation) return;
    if (!rawPhone) {
      toast.warning('Telefone não encontrado para este contato.');
      return;
    }
    setIsBlocking(true);
    try {
      if (isBlocked) {
        const removed = await unblockContactByPhone(rawPhone);
        if (!removed) {
          toast.warning('Não foi possível reativar. Contato não encontrado na lista de bloqueados.');
        } else {
          setBlockedOverride(false);
        }
      } else {
        await blockContactByPhone(rawPhone, getDisplayName(selectedConversation));
        setBlockedOverride(true);
      }
    } catch (error) {
      console.error('Error toggling blocked contact:', error);
      toast.error('Erro ao atualizar o bloqueio do contato.');
    }
    setIsBlocking(false);
  };

  const filteredShortcuts = useMemo(
    () => filterShortcutsByPrefix(shortcuts, newMessage),
    [newMessage, shortcuts],
  );

  useEffect(() => {
    if (showShortcuts && filteredShortcuts.length > 0) {
      setHighlightedIndex((prev) => {
        if (prev >= 0 && prev < filteredShortcuts.length) return prev;
        return 0;
      });
    } else {
      setHighlightedIndex(-1);
    }
  }, [filteredShortcuts, showShortcuts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const shouldShow = value.startsWith('/') && value.length > 0;
    setNewMessage(value);
    setShowShortcuts(shouldShow);
    if (!shouldShow) setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const hasDropdown = showShortcuts && filteredShortcuts.length > 0;

    if (hasDropdown && e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredShortcuts.length);
      return;
    }

    if (hasDropdown && e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + filteredShortcuts.length) % filteredShortcuts.length);
      return;
    }

    if (hasDropdown && e.key === 'Enter') {
      const shortcut = filteredShortcuts[highlightedIndex] || filteredShortcuts[0];
      if (shortcut) {
        e.preventDefault();
        setNewMessage(shortcut.mensagem);
        setShowShortcuts(false);
      }
      return;
    }

    if (e.key === ' ' || e.key === 'Enter') {
      const shortcut = shortcuts.find(s => `/${s.atalho}` === newMessage);
      if (shortcut) {
        e.preventDefault();
        setNewMessage(shortcut.mensagem);
        setShowShortcuts(false);
      }
    }
  };

  const handleNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Detectar clique fora do componente de edição
  useReactEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditingName && editContainerRef.current && !editContainerRef.current.contains(event.target as Node)) {
        handleCancelEdit();
      }
    };

    if (isEditingName) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingName]);

  const listArea = (
    <ConversationsListPanel
      conversations={conversations}
      selectedId={selectedConversation?.id}
      isMobile={isMobile}
      isReactivatingAll={isReactivatingAll}
      onReactivateAll={handleReactivateAll}
      onSelectConversation={handleSelectConversation}
      newMessageModalOpen={newMessageModalOpen}
      onOpenNewMessageModal={() => setNewMessageModalOpen(true)}
      onCloseNewMessageModal={() => setNewMessageModalOpen(false)}
      onSendNewMessage={handleSendNewMessage}
    />
  );

  const chatPane = selectedConversation ? (
    <div className={`flex flex-col flex-1 min-w-0 ${isMobile ? 'h-full min-h-0' : ''}`}>
      <ConversationsChatHeader
        conversation={selectedConversation}
        isMobile={isMobile}
        isEditingName={isEditingName}
        editedName={editedName}
        setEditedName={setEditedName}
        editContainerRef={editContainerRef}
        isBlocked={isBlocked}
        isBlocking={isBlocking}
        onPhotoClick={handlePhotoClick}
        onPhotoKeyDown={handlePhotoKeyDown}
        onPhotoChange={handlePhotoChange}
        onNameKeyPress={handleNameKeyPress}
        onSaveName={handleSaveName}
        onEditName={handleEditName}
        onToggleBlocked={handleToggleBlocked}
        onToggleBot={handleToggleBot}
      />

      <ConversationsMessageThread
        key={`${selectedConversation.id}-${selectedConversation.botActive ? 'bot' : 'manual'}`}
        messages={messages}
        messagesEndRef={messagesEndRef}
        onImprove={handleOpenBotImprovement}
        onTeachResponse={handleOpenClientImprovement}
        unreadCount={selectedConversation.botActive ? 0 : selectedConversation.unreadCount}
      />

      <ConversationsComposer
        isMobile={isMobile}
        onSubmit={handleSendMessage}
        showImagePanel={showImagePanel}
        imageFile={imageFile}
        onPickPhoto={handlePickPhoto}
        isImageSending={isImageSending}
        onSendImage={handleSendImage}
        onCancelImage={handleCancelImage}
        imageUrl={imageUrl}
        imageError={imageError}
        showAudioPanel={showAudioPanel}
        isRecording={isRecording}
        audioBlob={audioBlob}
        onStartRecording={handleStartRecording}
        isAudioSending={isAudioSending}
        onStopRecording={handleStopRecording}
        onSendAudio={handleSendAudio}
        onCancelAudio={handleCancelAudio}
        recordingMs={recordingMs}
        audioUrl={audioUrl}
        audioError={audioError}
        showShortcuts={showShortcuts}
        filteredShortcuts={filteredShortcuts}
        highlightedIndex={highlightedIndex}
        setNewMessage={setNewMessage}
        setShowShortcuts={setShowShortcuts}
        setHighlightedIndex={setHighlightedIndex}
        inputRef={inputRef}
        newMessage={newMessage}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onToggleImagePanel={handleToggleImagePanel}
        onToggleAudioPanel={handleToggleAudioPanel}
        fileInputRef={fileInputRef}
        onImageSelected={handleImageSelected}
      />
    </div>
  ) : (
    <ConversationsEmptyState conversationsCount={conversations.length} />
  );

  const mobileToggle = (
    <ConversationsMobileToggle
      mobileView={mobileView}
      hasSelectedConversation={!!selectedConversation}
      onToggle={handleMobileToggle}
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Layout>
      <ConfirmModal
        open={reactivateAllOpen}
        variant="primary"
        title="Reativar IA em todas as conversas?"
        description="O atendimento por IA será reativado para todos os contatos que estavam pausados."
        confirmLabel={isReactivatingAll ? 'Reativando...' : 'Reativar todas'}
        saving={isReactivatingAll}
        onCancel={() => { if (!isReactivatingAll) setReactivateAllOpen(false); }}
        onConfirm={handleReactivateAllConfirm}
      />
      {isMobile ? (
        <div className="h-full flex flex-col overflow-hidden bg-white rounded-lg shadow-sm">
          {mobileToggle}
          <div className="flex flex-col flex-1 min-h-0">
            {mobileView === 'chat' && selectedConversation ? (
              chatPane
            ) : (
              <div className="flex flex-col h-full min-h-0">
                {listArea}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="h-full flex overflow-hidden bg-white rounded-lg shadow-sm">
          <div className="flex flex-col w-80 border-r border-gray-200">
            {listArea}
          </div>
          {chatPane}
        </div>
      )}
      <ConversationsImprovementModal
        open={isImprovementOpen}
        improvementKind={improvementKind}
        improvementMessage={improvementMessage}
        improvementText={improvementText}
        setImprovementText={setImprovementText}
        improvementError={improvementError}
        improvementSuccess={improvementSuccess}
        isImprovementSending={isImprovementSending}
        onClose={handleCloseImprovement}
        onSubmit={handleSubmitImprovement}
      />
      <ConversationsPhotoPreviewModal
        open={isPhotoPreviewOpen}
        src={previewPhotoSrc}
        alt={previewPhotoAlt}
        onClose={closePhotoPreview}
      />
    </Layout>
  );
};

export default Conversations;
